import { useEffect, useMemo, useRef, useState } from 'react';
import { marked, Renderer } from 'marked';
import type { Source } from '@/shared/types';
import { formatHHMMSS, prettyVideoTitle, slugify as sharedSlugify } from '@/shared/text';

// Imperative handle the parent uses to scroll within the rendered content.
// Heading anchors come from the summary as kebab-case slugs; transcript
// timestamps come as "HH:MM:SS" and we look up the nearest cue.
export interface ChapterContentHandle {
  scrollToHeading(slug: string): boolean;
  scrollToTimestamp(seconds: number): boolean;
  // Highlight the query terms across the chapter view and scroll the first
  // match into view (used when opening a chapter from the Search tab).
  scrollToText(query: string): boolean;
}

export function ChapterContent({
  sources,
  containerRef,
  onReady,
  onTimestampClick,
}: {
  sources: Source[];
  containerRef: React.RefObject<HTMLDivElement>;
  onReady?: (handle: ChapterContentHandle) => void;
  onTimestampClick?: (seconds: number) => void;
}) {
  const localRef = useRef<HTMLDivElement>(null);
  const ref = containerRef ?? localRef;
  // Wraps the whole chapter view (text + transcripts) so search highlight can
  // span both. Cleared on unmount.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => () => clearSearchHighlight(), []);

  const textSources = sources.filter((s) => s.type === 'chapter' && (s.raw_markdown ?? '').length > 0);
  const videoSources = sources.filter((s) => s.type === 'video' && (s.vtt_cues?.length ?? 0) > 0);

  // Render markdown to HTML once per source set, with stable heading ids so
  // summary anchors can scroll to them.
  const html = useMemo(() => renderTextSources(textSources), [textSources]);
  const combinedMarkdown = useMemo(
    () =>
      textSources
        .map((s) => `# ${s.title}\n\n${s.raw_markdown ?? ''}`)
        .join('\n\n---\n\n'),
    [textSources]
  );

  useEffect(() => {
    if (!onReady) return;
    onReady({
      scrollToHeading: (slug) => scrollToSlug(ref.current, slug),
      scrollToTimestamp: (seconds) => scrollToCue(ref.current, videoSources, seconds),
      scrollToText: (query) => highlightAndScroll(rootRef.current, query),
    });
  }, [onReady, html, videoSources, ref]);

  if (textSources.length === 0 && videoSources.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4" ref={rootRef}>
      {html && (
        <details open className="rounded border border-slate-200 bg-card">
          <summary className="flex cursor-pointer items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            <span>Chapter text</span>
            <CopyButton getText={() => combinedMarkdown} label="Copy markdown" />
          </summary>
          <div
            ref={ref}
            className="prose-sm max-h-[50vh] overflow-auto px-3 py-3 text-sm leading-relaxed text-slate-800 [&_h1]:mt-3 [&_h1]:text-lg [&_h1]:font-bold [&_h2]:mt-3 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:font-semibold [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_strong]:font-semibold [&_em]:italic [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs [&_a]:text-blue-700 [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </details>
      )}

      {videoSources.map((src) => (
        <TranscriptView key={src.id} source={src} onTimestampClick={onTimestampClick} />
      ))}
    </div>
  );
}

function TranscriptView({
  source,
  onTimestampClick,
}: {
  source: Source;
  onTimestampClick?: (seconds: number) => void;
}) {
  const cues = source.vtt_cues ?? [];
  if (cues.length === 0) return null;
  const transcriptText = cues
    .map((c) => `[${formatHHMMSS(c.start)}] ${c.text}`)
    .join('\n');
  return (
    <details className="rounded border border-slate-200 bg-card">
      <summary className="flex cursor-pointer items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
        <span>
          Transcript · {prettyTitle(source.title)}{' '}
          <span className="ml-1 font-normal text-slate-400">({cues.length} cues)</span>
        </span>
        <CopyButton getText={() => transcriptText} label="Copy transcript" />
      </summary>
      <ol
        className="max-h-[50vh] overflow-auto px-3 py-2 text-sm"
        data-source-id={source.id}
      >
        {cues.map((c, i) => (
          <li
            key={`${source.id}-${i}`}
            id={cueId(source.id, c.start)}
            data-start={c.start}
            className="grid grid-cols-[auto_1fr] gap-2 border-l-2 border-transparent py-0.5 pl-2 target:border-blue-500 target:bg-blue-50"
          >
            <button
              onClick={() => onTimestampClick?.(c.start)}
              disabled={!onTimestampClick}
              className="select-none rounded px-1 py-0 text-left font-mono text-[10px] text-slate-500 hover:bg-blue-100 hover:text-blue-700 disabled:cursor-default disabled:text-slate-400 disabled:hover:bg-transparent"
              title={
                onTimestampClick
                  ? 'Seek the video in the LMS tab (and scroll the transcript here).'
                  : 'Open the LMS video tab to enable seeking.'
              }
            >
              {formatHHMMSS(c.start)}
            </button>
            <span className="text-slate-800">{c.text}</span>
          </li>
        ))}
      </ol>
    </details>
  );
}

function renderTextSources(sources: Source[]): string {
  if (sources.length === 0) return '';
  const renderer = new Renderer();
  const orig = renderer.heading.bind(renderer);
  renderer.heading = (token) => {
    const slug = slugify(extractText(token));
    const html = orig(token);
    // Inject id="h-<slug>" into the rendered tag.
    return html.replace(/^<(h[1-6])(\s|>)/, (_m, tag, sep) => `<${tag} id="h-${slug}"${sep}`);
  };
  marked.setOptions({ async: false, gfm: true, breaks: false });
  const md = sources
    .map((s) => `# ${s.title}\n\n${s.raw_markdown ?? ''}`)
    .join('\n\n---\n\n');
  return marked.parse(md, { renderer }) as string;
}

function extractText(token: { tokens?: { raw?: string; text?: string }[]; text?: string }): string {
  if (typeof token.text === 'string') return token.text;
  if (Array.isArray(token.tokens)) {
    return token.tokens.map((t) => t.text ?? t.raw ?? '').join('');
  }
  return '';
}

// Re-export so existing imports keep working while internals migrate.
export const slugify = sharedSlugify;

function cueId(sourceId: string, start: number): string {
  return `t-${sourceId}-${Math.floor(start)}`;
}

// Name registered with the CSS Custom Highlight API; styled in styles.css via
// ::highlight(tishnotes-search).
const SEARCH_HIGHLIGHT = 'tishnotes-search';

// Newer DOM APIs not in all TS lib targets — accessed loosely.
function highlightApi(): { set: (n: string, h: object) => void; delete: (n: string) => void } | null {
  const c = CSS as unknown as { highlights?: { set: (n: string, h: object) => void; delete: (n: string) => void } };
  const HighlightCtor = (globalThis as { Highlight?: unknown }).Highlight;
  if (!c.highlights || typeof HighlightCtor !== 'function') return null;
  return c.highlights;
}

function clearSearchHighlight(): void {
  highlightApi()?.delete(SEARCH_HIGHLIGHT);
}

// highlightAndScroll marks every occurrence of the query terms within `root`
// (no DOM mutation — uses the CSS Custom Highlight API) and scrolls the first
// match into view, opening any collapsed <details> around it. Returns whether
// anything matched.
function highlightAndScroll(root: HTMLElement | null, query: string): boolean {
  if (!root) return false;
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);
  const api = highlightApi();
  api?.delete(SEARCH_HIGHLIGHT);
  if (terms.length === 0) return false;

  const ranges: Range[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.nodeValue ?? '';
    if (!text.trim()) continue;
    const lower = text.toLowerCase();
    for (const term of terms) {
      let i = lower.indexOf(term);
      while (i >= 0) {
        const r = document.createRange();
        r.setStart(node, i);
        r.setEnd(node, i + term.length);
        ranges.push(r);
        i = lower.indexOf(term, i + term.length);
      }
    }
  }
  if (ranges.length === 0) return false;

  if (api) {
    const HighlightCtor = (globalThis as { Highlight: new (...r: Range[]) => object }).Highlight;
    api.set(SEARCH_HIGHLIGHT, new HighlightCtor(...ranges));
  }

  // Scroll the earliest match (TreeWalker is document order, so ranges[0]) into
  // view, opening any collapsed <details> ancestors first.
  const anchor = ranges[0].startContainer.parentElement;
  if (anchor) {
    for (
      let d = anchor.closest('details') as HTMLDetailsElement | null;
      d;
      d = d.parentElement?.closest('details') as HTMLDetailsElement | null
    ) {
      d.open = true;
    }
    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  return true;
}

function scrollToSlug(root: HTMLElement | null, slug: string): boolean {
  if (!root) return false;
  const el = root.querySelector(`#h-${CSS.escape(slug)}`);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.classList.add('bg-yellow-100');
  setTimeout(() => el.classList.remove('bg-yellow-100'), 1500);
  return true;
}

function scrollToCue(_root: HTMLElement | null, sources: Source[], seconds: number): boolean {
  // The chapter-text container is unrelated to transcript lists; we look up
  // matching cues across the whole document.
  for (const src of sources) {
    const cues = src.vtt_cues ?? [];
    if (cues.length === 0) continue;
    // Find the cue whose [start, end] contains seconds, or the closest by start.
    let match = cues.find((c) => seconds >= c.start && seconds <= c.end);
    if (!match) {
      let best = cues[0];
      let bestDelta = Math.abs(seconds - best.start);
      for (const c of cues) {
        const d = Math.abs(seconds - c.start);
        if (d < bestDelta) {
          best = c;
          bestDelta = d;
        }
      }
      match = best;
    }
    if (!match) continue;
    const el = document.getElementById(cueId(src.id, match.start));
    if (!el) continue;
    // Open the parent <details> if it's collapsed, then scroll.
    const details = el.closest('details');
    if (details && !details.open) details.open = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('bg-yellow-100');
    setTimeout(() => el.classList.remove('bg-yellow-100'), 1500);
    return true;
  }
  return false;
}

function CopyButton({
  getText,
  label,
}: {
  getText: () => string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);
  function copy(e: React.MouseEvent) {
    // Don't toggle the parent <details> when the button is clicked.
    e.preventDefault();
    e.stopPropagation();
    void navigator.clipboard.writeText(getText()).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  }
  return (
    <button
      onClick={copy}
      className="rounded border border-slate-300 bg-card px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-slate-700 hover:bg-slate-50"
      title={label}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// Source titles arrive as either a URL (when stubbed from a video src) or a
// plain string. prettyVideoTitle handles both cases.
const prettyTitle = prettyVideoTitle;
